/**
 * Vincent B679 (Eric 2026-02-XX) — Sous-onglet « Whisper » de la fiche vidéo.
 * Extrait de VideoDetailPage.js pour amincir le fichier principal
 * (-95 lignes). Contient la WhisperToolbar (actions éditeur) + le rendu
 * des segments en 3 colonnes [MM:SS] | Locuteur | Citation.
 *
 * Vincent B694 (Eric 2026-02-XX) — Ajoute l'édition d'un locuteur par
 * phrase : clic sur la pastille de locuteur → menu déroulant filtré sur
 * les locuteurs mappés depuis l'onglet « Locuteurs ». Sauvegarde
 * immédiate via PATCH /api/videos/:id/transcript-segments/:sid/speaker.
 */
import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import WhisperToolbar from "./WhisperToolbar";
import WebCorrectionBadge from "./WebCorrectionBadge";
import { FileText, PencilSimple, X } from "@phosphor-icons/react";
import { formatSpeakerLines } from "../../lib/speakerFormat";

// Vincent #100 (2026-05-12) — Format 3 colonnes uniforme.
function formatTimeBracket(seconds) {
  if (seconds == null || isNaN(seconds)) return "[00:00]";
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `[${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}]`;
}

/**
 * Vincent B694 — Sous-composant : Pastille locuteur cliquable devenant un
 * menu déroulant à la demande. Isolée pour éviter les re-renders massifs
 * sur toute la liste quand on ouvre/ferme un éditeur.
 */
function SpeakerCell({
  segment,
  speakerColor,
  canEditKnowledge,
  api,
  videoId,
  speakerOptions,
  onChanged,
  realIdx,
  lang,
  onNewSpeakerCreated,
}) {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  // Vincent B696-enhancement (Eric 2026-02-08) — Mini-modal inline pour
  // créer un nouveau locuteur (POST /api/videos/speakers) sans quitter
  // l'onglet Whisper. Le nom fraîchement créé est auto-appliqué au
  // segment courant.
  const [showNewSpeakerModal, setShowNewSpeakerModal] = useState(false);
  const [newSpeakerName, setNewSpeakerName] = useState("");
  const [newSpeakerRole, setNewSpeakerRole] = useState("");
  const [creatingSpeaker, setCreatingSpeaker] = useState(false);

  const displayName = segment.speaker || "—";

  const save = async (newName) => {
    if (!newName || newName === segment.speaker) {
      setEditing(false);
      return;
    }
    setSaving(true);
    try {
      await axios.patch(
        `${api}/api/videos/${videoId}/transcript-segments/${segment.id}/speaker`,
        { speaker: newName },
        { withCredentials: true },
      );
      toast.success(
        lang === "fr"
          ? `Locuteur mis à jour : « ${newName} »`
          : `Speaker updated: "${newName}"`,
      );
      setEditing(false);
      if (typeof onChanged === "function") await onChanged();
    } catch (err) {
      toast.error(
        (lang === "fr" ? "Erreur : " : "Error: ") +
          (err?.response?.data?.detail || err.message),
      );
    } finally {
      setSaving(false);
    }
  };

  const handleSelectChange = (val) => {
    // B696-enhancement : marker spécial pour ouvrir le modal de création
    if (val === "__ADD_NEW__") {
      setShowNewSpeakerModal(true);
      return;
    }
    save(val);
  };

  const createNewSpeaker = async () => {
    const name = newSpeakerName.trim();
    if (!name) {
      toast.error(lang === "fr" ? "Nom requis" : "Name required");
      return;
    }
    setCreatingSpeaker(true);
    try {
      // 1) Crée l'entrée dans la banque de locuteurs de l'org
      // Vincent B696-enhancement complément (Eric 2026-02-10) — Persiste
      // l'origine pour permettre à la banque de locuteurs d'afficher un
      // badge « ✎ Créé depuis Whisper » avec lien vers la vidéo + segment.
      await axios.post(
        `${api}/api/videos/speakers`,
        {
          name,
          role: newSpeakerRole.trim(),
          origin_video_id: videoId,
          origin_segment_id: segment?.id || "",
          origin_timestamp: typeof segment?.start === "number" ? segment.start : 0,
        },
        { withCredentials: true },
      );
      // 2) Refresh de la banque côté parent (loadSpeakerData)
      if (typeof onNewSpeakerCreated === "function") await onNewSpeakerCreated();
      // 3) Applique le nouveau nom au segment courant
      await save(name);
      // 4) Reset modal
      setShowNewSpeakerModal(false);
      setNewSpeakerName("");
      setNewSpeakerRole("");
    } catch (err) {
      toast.error(
        (lang === "fr" ? "Erreur : " : "Error: ") +
          (err?.response?.data?.detail || err.message),
      );
    } finally {
      setCreatingSpeaker(false);
    }
  };

  const editable = canEditKnowledge && segment.id;

  if (editing) {
    return (
      <>
        <div
          className="text-[12px] leading-relaxed flex items-center gap-1"
          onClick={(e) => e.stopPropagation()}
          data-testid={`segment-speaker-editor-${realIdx}`}
        >
          <select
            className="text-[12px] font-semibold border border-[#002FA7] rounded px-1.5 py-0.5 bg-white max-w-[110px]"
            defaultValue={segment.speaker || ""}
            onChange={(e) => handleSelectChange(e.target.value)}
            disabled={saving}
            autoFocus
            data-testid={`segment-speaker-select-${realIdx}`}
          >
            <option value="" disabled>
              {lang === "fr" ? "— Choisir —" : "— Choose —"}
            </option>
            {speakerOptions.map((name) => (
              <option key={name} value={name}>
                {name}
              </option>
            ))}
            {/* Vincent B696-enhancement — Toujours proposer d'ajouter un
                nouveau locuteur en fin de liste. Séparateur visuel. */}
            <option disabled>──────────</option>
            <option value="__ADD_NEW__">
              {lang === "fr" ? "+ Ajouter un nouveau locuteur…" : "+ Add new speaker…"}
            </option>
          </select>
          <button
            type="button"
            onClick={() => setEditing(false)}
            disabled={saving}
            className="text-zinc-400 hover:text-zinc-700 shrink-0"
            title={lang === "fr" ? "Annuler" : "Cancel"}
            data-testid={`segment-speaker-cancel-${realIdx}`}
          >
            <X size={12} />
          </button>
        </div>

        {/* Mini-modal de création — overlay léger */}
        {showNewSpeakerModal && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
            onClick={(e) => {
              e.stopPropagation();
              if (!creatingSpeaker) setShowNewSpeakerModal(false);
            }}
            data-testid={`new-speaker-modal-${realIdx}`}
          >
            <div
              className="bg-white rounded-lg shadow-xl w-[380px] max-w-[92vw] p-5"
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="text-base font-bold text-zinc-900 mb-1">
                {lang === "fr" ? "Nouveau locuteur" : "New speaker"}
              </h3>
              <p className="text-xs text-zinc-500 mb-4">
                {lang === "fr"
                  ? "Sera ajouté à la banque de locuteurs de l'organisation et appliqué immédiatement à cette phrase."
                  : "Will be added to the organization's speaker bank and applied to this sentence immediately."}
              </p>
              <label className="block text-xs font-semibold text-zinc-700 mb-1">
                {lang === "fr" ? "Nom" : "Name"} *
              </label>
              <input
                type="text"
                value={newSpeakerName}
                onChange={(e) => setNewSpeakerName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !creatingSpeaker) createNewSpeaker();
                  if (e.key === "Escape") setShowNewSpeakerModal(false);
                }}
                className="w-full text-sm border border-zinc-300 rounded px-3 py-2 mb-3 focus:border-[#002FA7] outline-none"
                placeholder={lang === "fr" ? "ex. Jean-Sébastien Roy" : "e.g. Jane Doe"}
                autoFocus
                disabled={creatingSpeaker}
                data-testid={`new-speaker-name-input-${realIdx}`}
              />
              <label className="block text-xs font-semibold text-zinc-700 mb-1">
                {lang === "fr" ? "Rôle" : "Role"}{" "}
                <span className="text-zinc-400 font-normal">
                  ({lang === "fr" ? "optionnel" : "optional"})
                </span>
              </label>
              <input
                type="text"
                value={newSpeakerRole}
                onChange={(e) => setNewSpeakerRole(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !creatingSpeaker) createNewSpeaker();
                  if (e.key === "Escape") setShowNewSpeakerModal(false);
                }}
                className="w-full text-sm border border-zinc-300 rounded px-3 py-2 mb-4 focus:border-[#002FA7] outline-none"
                placeholder={lang === "fr" ? "ex. Conseiller, Client, Coach" : "e.g. Advisor, Client, Coach"}
                disabled={creatingSpeaker}
                data-testid={`new-speaker-role-input-${realIdx}`}
              />
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setShowNewSpeakerModal(false)}
                  disabled={creatingSpeaker}
                  className="text-sm px-3 py-1.5 rounded border border-zinc-300 hover:bg-zinc-50 text-zinc-700"
                  data-testid={`new-speaker-cancel-${realIdx}`}
                >
                  {lang === "fr" ? "Annuler" : "Cancel"}
                </button>
                <button
                  type="button"
                  onClick={createNewSpeaker}
                  disabled={creatingSpeaker || !newSpeakerName.trim()}
                  className="text-sm px-3 py-1.5 rounded bg-[#002FA7] hover:bg-[#001d70] text-white disabled:opacity-50"
                  data-testid={`new-speaker-create-${realIdx}`}
                >
                  {creatingSpeaker
                    ? (lang === "fr" ? "Création…" : "Creating…")
                    : (lang === "fr" ? "Créer et appliquer" : "Create and apply")}
                </button>
              </div>
            </div>
          </div>
        )}
      </>
    );
  }

  return (
    <div
      className="text-[12px] font-semibold leading-relaxed truncate flex items-center gap-1.5"
      style={{ color: speakerColor }}
      data-testid={`segment-speaker-${realIdx}`}
      title={
        editable
          ? (lang === "fr"
              ? `Clic pour changer le locuteur de cette phrase (${displayName})`
              : `Click to change speaker for this sentence (${displayName})`)
          : displayName
      }
    >
      {editable ? (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            setEditing(true);
          }}
          className="text-left hover:underline decoration-dotted underline-offset-2 flex items-start gap-1 min-w-0"
          data-testid={`segment-speaker-btn-${realIdx}`}
        >
          {/* B776 (Eric 2026-02-17 SPEC STRICK PROD) — Speaker sur 2 lignes */}
          <span className="leading-tight break-words min-w-0">
            {formatSpeakerLines(displayName).map((line, li) => (
              <span key={li} className="block">{line}</span>
            ))}
          </span>
          <PencilSimple size={10} className="opacity-40 shrink-0 mt-0.5" />
        </button>
      ) : (
        <span className="leading-tight break-words min-w-0">
          {formatSpeakerLines(displayName).map((line, li) => (
            <span key={li} className="block">{line}</span>
          ))}
        </span>
      )}
      {/* Vincent B693 (Eric 2026-02-08) — Badge de confiance diarisation. */}
      {typeof segment.speaker_confidence === "number" && (
        <span
          className={`inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-medium ${
            segment.speaker_confidence >= 90
              ? "bg-emerald-100 text-emerald-700"
              : segment.speaker_confidence >= 70
              ? "bg-amber-100 text-amber-700"
              : "bg-orange-100 text-orange-700"
          }`}
          title={`Confiance AssemblyAI : ${segment.speaker_confidence.toFixed(1)} %`}
          data-testid={`segment-confidence-${realIdx}`}
        >
          {Math.round(segment.speaker_confidence)}%
        </span>
      )}
      {segment.speaker_manually_edited && (
        <span
          className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-medium bg-violet-100 text-violet-700"
          title={
            lang === "fr"
              ? "Locuteur modifié manuellement pour cette phrase"
              : "Speaker manually edited for this sentence"
          }
          data-testid={`segment-edited-badge-${realIdx}`}
        >
          {lang === "fr" ? "✎ édité" : "✎ edited"}
        </span>
      )}
    </div>
  );
}

export default function WhisperSubTab({
  show,
  // Vincent B954 (Eric 2026-02-14 SPEC STRICK PROD) — `render` splittable :
  //   undefined = tout rendu (legacy)
  //   "toolbar"  = uniquement la WhisperToolbar (haut du panneau)
  //   "segments" = uniquement le container des segments (sous SpeakerLegendBox)
  render,
  // Toolbar props
  canEditKnowledge,
  lang,
  api,
  videoId,
  setShowHistory,
  loadRevisions,
  loadVideoData,
  loadAllTranscripts,
  setUrlProposals,
  urlValidating,
  setUrlValidating,
  startPolling,
  useDiarisation,
  useFacialId,
  // Vincent B778 v2 (Eric 2026-02-18 SPEC STRICK PROD) — Durée vidéo (s)
  // passée au TranscriptionSettingsModal pour borner l'auto-retry A/B.
  videoDuration = 0,
  // Transcript props
  transcriptContainerRef,
  filteredSegments,
  data,
  activeIdx,
  speakerMap,
  seekTo,
  activeSegmentRef,
  transcriptSearch,
  highlightText,
  isProcessing,
  allTranscripts,
  // Vincent B945v2 (Eric 2026-02-14 SPEC STRICK PROD) — Passe-plat vers
  // WhisperToolbar : injecte les boutons fusion (Reglages, Refaire) juste
  // avant « URLs » dans la même toolbar.
  extraFusionButtons = null,
  // Vincent B694 — locuteurs mappés depuis l'onglet Locuteurs (source unique)
  speakerOptions = [],
  // Vincent B696-enhancement — callback pour recharger la banque après
  // création d'un nouveau locuteur via le mini-modal SpeakerCell
  reloadSpeakerData,
  // fmt utilities passed in when parent uses a different formatter
  formatTimeBracket: fmtOverride,
}) {
  if (!show) return null;
  const fmtTime = fmtOverride || formatTimeBracket;

  // Callback appelé après édition speaker d'un segment : re-fetch les
  // données de la vidéo pour rafraîchir tous les segments affichés.
  const refreshAfterEdit = async () => {
    if (typeof loadVideoData === "function") await loadVideoData();
    if (typeof loadAllTranscripts === "function") await loadAllTranscripts();
  };

  return (
    <>
      {/* Vincent B954 (Eric 2026-02-14 SPEC STRICK PROD) — Prop `render`
          permet de rendre uniquement la toolbar (au-dessus du tableau
          de locuteurs, single-block) ou uniquement les segments
          (en-dessous). `render === undefined` = comportement legacy. */}
      {(render === undefined || render === "toolbar") && (
        <WhisperToolbar
        show={canEditKnowledge}
        lang={lang}
        api={api}
        videoId={videoId}
        setShowHistory={setShowHistory}
        loadRevisions={loadRevisions}
        loadVideoData={loadVideoData}
        loadAllTranscripts={loadAllTranscripts}
        setUrlProposals={setUrlProposals}
        urlValidating={urlValidating}
        setUrlValidating={setUrlValidating}
        startPolling={startPolling}
        useDiarisation={useDiarisation}
        useFacialId={useFacialId}
        videoDuration={videoDuration}
        // Vincent B787 (Eric 2026-02-18 SPEC STRICK PROD) — Heuristique
        // « transcription suspecte » : vidéo > 5 min avec < 20 segments
        // Whisper finaux. Fait clignoter le bouton Diagnostic pour
        // attirer l'attention sur les cas où le pipeline s'est arrêté
        // sans erreur mais avec très peu de contenu (paused_pending_fusion
        // sur un audio de coaching victime d'aggressive_dedup, MP3
        // tronqué, etc.). Ratio < 4 segments/min = anormalement faible.
        diagnosticSuspicious={
          videoDuration > 300
          && (allTranscripts?.whisper?.segments?.length || 0) < 20
          && (allTranscripts?.whisper?.segments?.length || 0) > 0
        }
        extraFusionButtons={extraFusionButtons}
        // Vincent B971 (Eric 2026-02-26 SPEC STRICK PROD) — Passe
        // allTranscripts pour activer le pattern « toujours visible +
        // disabled » quand has_whisper est absent.
        allTranscripts={allTranscripts}
      />
      )}
      {/* Vincent B954 — Le container segments est rendu si `render === undefined`
          (legacy) ou `render === "segments"`. */}
      {(render === undefined || render === "segments") && (
      <>
      {/* Vincent B935 (Eric 2026-02-14 SPEC STRICK PROD) — Les bannières
          amber de diagnostic (dedup_warning + filter_warning) sont retirées
          au profit du bouton unique « Diagnostic Whisper » dans la
          WhisperToolbar (qui ouvre déjà un modal détaillé avec toutes ces
          infos, sans surcharger le layout). */}
      <div
        ref={transcriptContainerRef}
        className="max-h-[calc(100vh-360px)] min-h-[300px] overflow-y-auto scroll-smooth"
        data-testid="transcript-container"
      >
        {filteredSegments.length > 0 ? (
          <div className="px-3 py-3" data-testid="whisper-segments-3col">
            {/* Vincent #100 (2026-05-12) — Format 3 colonnes uniforme :
                [MM:SS] | Locuteur | Citation. Sans lignes séparatrices,
                mêmes police/taille/couleurs que Fireflies/Fusionnée.
                Click n'importe où sur la ligne = seek vidéo. */}
            {filteredSegments.map((segment) => {
              const realIdx = data.segments.indexOf(segment);
              const isActive = realIdx === activeIdx;
              const speakerColor = speakerMap[segment.speaker] || "#71717a";
              return (
                <div
                  key={realIdx}
                  ref={isActive ? activeSegmentRef : null}
                  className={`grid grid-cols-[70px_110px_1fr] gap-3 items-start py-1.5 cursor-pointer rounded transition-colors ${
                    isActive ? "bg-[#002FA7]/8" : "hover:bg-zinc-50"
                  }`}
                  onClick={() => seekTo(segment.start)}
                  data-testid={`segment-${realIdx}`}
                  data-temps={segment.start ?? ""}
                  data-focus-target={`segment-t-${Math.floor(segment.start || 0)}`}
                >
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); seekTo(segment.start); }}
                    className={`text-[11px] font-mono text-left tabular-nums leading-relaxed ${
                      isActive ? "text-[#002FA7] font-bold" : "text-zinc-400 hover:text-[#002FA7]"
                    }`}
                    data-testid={`segment-timecode-${realIdx}`}
                  >
                    {fmtTime(segment.start)}
                  </button>
                  <SpeakerCell
                    segment={segment}
                    speakerColor={speakerColor}
                    canEditKnowledge={canEditKnowledge}
                    api={api}
                    videoId={videoId}
                    speakerOptions={speakerOptions}
                    onChanged={refreshAfterEdit}
                    onNewSpeakerCreated={reloadSpeakerData}
                    realIdx={realIdx}
                    lang={lang}
                  />
                  <div
                    className={`text-sm leading-relaxed ${isActive ? "text-zinc-900 font-medium" : "text-zinc-700"}`}
                    data-testid={`segment-text-${realIdx}`}
                  >
                    {transcriptSearch ? highlightText(segment.text, transcriptSearch) : segment.text}
                  </div>
                  {/* Vincent B943 (Eric 2026-02-14) — Encadré correction factuelle
                      quand une correction fact-check a été appliquée sur ce
                      segment (via /synthesis/apply-corrections). */}
                  <WebCorrectionBadge corrections={segment.web_corrections} lang={lang} />
                </div>
              );
            })}
          </div>
        ) : (
          <div className="p-10 text-center text-zinc-500">
            <FileText size={36} className="mx-auto mb-3 opacity-30" />
            <p className="text-sm font-medium text-zinc-700">
              {isProcessing
                ? (lang === "fr" ? "Transcription Whisper en cours…" : "Whisper transcription in progress…")
                : transcriptSearch
                  ? (lang === "fr" ? "Aucun résultat" : "No result")
                  : (lang === "fr" ? "Aucune transcription Whisper" : "No Whisper transcript")}
            </p>
            {/* Vincent B969 (Eric 2026-02-14 SPEC STRICK PROD) — Message
                actionnable si aucune transcription Whisper : dirige l'user
                vers le bouton « Refaire transcription Whisper » de la
                toolbar. */}
            {!isProcessing && !transcriptSearch && (
              <p className="mt-2 text-xs text-zinc-500 max-w-md mx-auto">
                {lang === "fr"
                  ? "Utilise le bouton « 🎙️ Refaire transcription Whisper » ci-dessus pour lancer la transcription automatique depuis l'audio de la vidéo."
                  : "Use the « 🎙️ Re-run Whisper transcription » button above to launch automatic transcription from the video's audio."}
              </p>
            )}
            {/* Vincent (2026-07-22) — Signaler la disponibilité immédiate de
                Fireflies pour ne pas faire attendre l'utilisateur pendant
                l'analyse. */}
            {isProcessing && allTranscripts?.summary?.has_fireflies && (
              <p className="mt-2 text-xs text-amber-600">
                💡 {lang === "fr"
                  ? "Une transcription Fireflies est déjà disponible — sélectionne « 🔥 Fireflies » dans le menu déroulant ci-dessus pour la consulter."
                  : "A Fireflies transcript is already available — select « 🔥 Fireflies » in the dropdown above."}
              </p>
            )}
          </div>
        )}
      </div>
      </>
      )}
    </>
  );
}
